import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { Filter as FilterIcon, X, Search, ChevronDown, ChevronRight, Star, Download } from 'lucide-react';
import { useData } from '../context/DataContext';
import FilterDropdown from './FilterDropdown';
import DetailDrawer from './DetailDrawer';
import { THEME_SHORTHAND, ACTION_SHORTHAND, themeColor, actionColor, COMMENTER_COLOR } from '../constants/chips';

// Read filter state from URL search params so a filtered view is shareable.
// Keys mirror the filter object: search (q), clusters, topics, recommended_actions
// (actions), commenter_types (types), featured, attachments_only (off via all=1),
// match, sort, group. Multi-value filters are comma-separated.

const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 30;
  background: var(--bg-page);
  border-bottom: 1px solid var(--color-border);
  padding: 0.75rem 24px;

  @media (max-width: 900px) {
    padding: 0.625rem 16px;
  }
`;

const TopBarInner = styled.div`
  max-width: var(--container-width);
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TopSearchWrap = styled.div`
  position: relative;
  flex: 1 1 360px;
  min-width: 240px;
`;

const TopSearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 36px 0 40px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--bg-card);
  font-family: var(--font-system);
  font-size: 0.9375rem;
  color: var(--color-text);
  outline: none;

  &:focus {
    border-color: var(--color-accent);
    box-shadow: 0 0 0 2px rgba(28, 117, 188, 0.15);
  }
`;

const TopSearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  pointer-events: none;
`;

const TopSearchClear = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 0;
  cursor: pointer;
  color: var(--color-text-muted);
  padding: 4px;
  display: flex;
  align-items: center;
  &:hover { color: var(--color-text); }
`;

const TopMeta = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-muted);
  white-space: nowrap;

  strong {
    color: var(--color-text);
    font-weight: 600;
  }
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 0.875rem;
  background: var(--bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-family: var(--font-system);
  font-size: 0.8125rem;
  color: var(--color-text);
  white-space: nowrap;

  &:hover { border-color: var(--color-accent); color: var(--color-accent); }
`;

const Layout = styled.div`
  display: flex;
  gap: 2rem;
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 1rem 24px 4rem 24px;

  @media (max-width: 900px) {
    padding: 1rem 16px 3rem 16px;
    gap: 1rem;
  }
`;

const GroupSection = styled.section`
  margin-bottom: 1.5rem;
`;

const GroupHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: var(--bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-family: var(--font-system);
  font-size: 0.9375rem;
  color: var(--color-text);
  cursor: pointer;
  text-align: left;
  margin-bottom: 0.625rem;

  &:hover {
    border-color: var(--color-accent);
  }
`;

const GroupName = styled.span`
  font-weight: 600;
`;

const GroupCount = styled.span`
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  margin-left: auto;
`;

// Lighter, indented sub-header for subtopics nested under a topic in the
// "Similar topics" grouped view.
const SubGroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.5rem 0 0.4rem 1rem;
  padding-left: 0.625rem;
  border-left: 2px solid var(--color-border);
  font-family: var(--font-system);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text-muted);

  .sub-count { font-weight: 400; }
`;

const SubGroupWrap = styled.div`
  margin-left: 1rem;
`;

const Main = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const MobileFilterButton = styled.button`
  display: none;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 0.875rem;
  background: var(--bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-family: var(--font-system);
  font-size: 0.875rem;
  color: var(--color-text);

  @media (max-width: 900px) {
    display: inline-flex;
  }
`;

const ActiveFilterPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 1rem;
`;

const ActivePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.25rem 0.5rem 0.25rem 0.625rem;
  background: ${p => p.$bg || 'rgba(28, 117, 188, 0.08)'};
  border: 1px solid ${p => p.$bg || 'rgba(28, 117, 188, 0.2)'};
  border-radius: 99px;
  color: ${p => p.$fg || 'var(--color-accent)'};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  font-family: var(--font-system);

  &:hover { filter: brightness(0.92); }
  svg { opacity: 0.85; }
`;

// Subtopic filters shown linked to their parent topic: a colored parent segment
// followed by one or more lighter subtopic segments, each notched into the one
// before it (puzzle-piece) and individually removable.
const CompoundPill = styled.div`
  display: inline-flex;
  align-items: stretch;
  border-radius: 99px;
  overflow: hidden;
  font-family: var(--font-system);
  font-size: 0.75rem;

  .seg { display: inline-flex; align-items: center; gap: 4px; padding: 0.25rem 0.6rem; border: 0; font-family: inherit; font-size: 0.75rem; }
  .parent { font-weight: 600; }
  button.parent { cursor: pointer; }
  button.parent:hover { filter: brightness(0.93); }
  /* Label-only parent (only a subtopic is filtered): dimmed + no remove button,
     to signal the topic as a whole is NOT selected. */
  .parent.label-only { opacity: 0.55; font-weight: 600; }

  button.child {
    background: #e5e7eb;
    color: #374151;
    font-weight: 500;
    font-family: inherit;
    font-size: 0.75rem;
    cursor: pointer;
    /* concave notch on the left edge so each segment "clicks into" its neighbor */
    clip-path: polygon(7px 0, 100% 0, 100% 100%, 7px 100%, 0 50%);
    margin-left: -7px;
    padding-left: 13px;
  }
  button.child:hover { filter: brightness(0.93); }
  svg { opacity: 0.8; }
`;

const Empty = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  color: var(--color-text-muted);
  background: var(--bg-card);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-lora);
  font-style: italic;
`;

const LoadMore = styled.button`
  margin: 1.5rem auto 0;
  display: block;
  padding: 0.5rem 1rem;
  background: var(--bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-family: var(--font-system);
  font-size: 0.875rem;
  color: var(--color-text);

  &:hover {
    border-color: var(--color-accent);
  }
`;

const SearchStatus = styled.div`
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  margin-bottom: 0.5rem;
`;

const FilterBar = styled.div`
  background: var(--bg-subtle);
  border-bottom: 1px solid var(--color-border);
  padding: 0.75rem 24px;

  @media (max-width: 900px) {
    padding: 0.625rem 16px;
    display: ${props => (props.$mobileOpen ? 'block' : 'none')};
  }
`;

const FilterRow = styled.div`
  max-width: var(--container-width);
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
`;

const InlineToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  cursor: pointer;
  input { accent-color: var(--color-accent); cursor: pointer; }
`;

const ResetLink = styled.button`
  margin-left: auto;
  background: none;
  border: 0;
  color: var(--color-accent);
  font-family: var(--font-system);
  font-size: 0.8125rem;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const TableScroll = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;

  /* Mobile: each row becomes its own card, so drop the wrapper chrome. */
  @media (max-width: 768px) {
    border: 0;
    border-radius: 0;
  }
`;

const ResultsTable = styled.table`
  width: 100%;
  table-layout: fixed;       /* fixed widths => columns wrap instead of overflowing */
  border-collapse: collapse;
  font-family: var(--font-system);
  font-size: 0.875rem;
  background: var(--bg-card);

  /* Topic chips wrap; FillChips clips to whole chips that fit the row height,
     and shows a "+N more" badge for the ones it couldn't fit. */
  .fill-chips { display: flex; flex-wrap: wrap; gap: 4px; align-content: flex-start; }
  .more-chip {
    font-size: 0.6875rem;
    font-weight: 500;
    padding: 1px 7px;
    border-radius: 99px;
    color: var(--color-text-muted);
    background: rgba(0, 0, 0, 0.05);
    white-space: nowrap;
  }

  /* Column widths: Summary is the widest so it reads as the primary column. */
  th:nth-child(1), td:nth-child(1) { width: 17%; }   /* Name */
  th:nth-child(2), td:nth-child(2) { width: 11%; }   /* Recommended action */
  th:nth-child(3), td:nth-child(3) { width: 41%; }   /* Summary */
  th:nth-child(4), td:nth-child(4) { width: 18%; }   /* Topic */
  th:nth-child(5), td:nth-child(5) { width: 13%; }   /* Commenter type */

  /* Row height is driven by the submitter Name (with a small floor). The Summary
     and Topic cells then FILL that height and clip what doesn't fit, so a tall
     row (long org name / many topics) shows more summary + tags, and a short one
     truncates — instead of a hardcoded line/chip cap leaving whitespace. Desktop
     only; on mobile the table reflows to stacked cards (below). */
  @media (min-width: 769px) {
    tbody td:first-child { height: 4.25rem; }              /* floor; Name grows it */
    td.col-summary, td.col-topic { position: relative; padding: 0; }
    td.col-summary .fill, td.col-topic .fill {
      position: absolute;
      inset: 0.625rem 0.875rem;
      overflow: hidden;
      margin: 0;
    }
    /* Summary fades its clipped bottom edge so a cut line reads as intentional.
       Topics do NOT fade — FillChips hides any chip that doesn't fully fit, so
       there's never a partial chip to soften. */
    td.col-summary .fill::after {
      content: '';
      position: absolute;
      left: 0; right: 0; bottom: 0;
      height: 1.1rem;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0), var(--bg-card));
      pointer-events: none;
    }
  }

  @media (max-width: 768px) {
    table-layout: auto;
    display: block;
    th:nth-child(n), td:nth-child(n) { width: auto; }
    /* Rows reflow into cards with per-cell data-labels, so the column header
       row is redundant — and left visible it overflows horizontally. Hide it. */
    thead { display: none; }
    tbody, tr { display: block; }
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 0.5rem 0.875rem;
  border-bottom: 1px solid var(--color-border);
  font-size: 0.6875rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  background: var(--bg-subtle);
`;

const SortableTh = styled(Th)`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  color: ${p => (p.$active ? 'var(--color-text)' : 'var(--color-text-muted)')};
  &:hover { color: var(--color-accent); }
`;

const SortArrow = styled.span`
  font-size: 0.75rem;
  margin-left: 2px;
`;

const Tr = styled.tr`
  cursor: pointer;
  border-bottom: 1px solid var(--color-border);
  &:last-child { border-bottom: 0; }
  &:hover { background: rgba(28, 93, 186, 0.045); }

  @media (max-width: 768px) {
    display: block;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    margin-bottom: 0.75rem;
    padding: 0.75rem 0.875rem;
    &:hover { background: var(--bg-subtle); }
  }
`;

const Td = styled.td`
  padding: 0.625rem 0.875rem;
  vertical-align: top;
  color: var(--color-text);

  @media (max-width: 768px) {
    display: block;
    width: auto !important;
    padding: 0.3rem 0;
    border: 0;

    &::before {
      content: attr(data-label);
      display: block;
      font-size: 0.625rem;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--color-text-muted);
      margin-bottom: 2px;
    }

    /* Name is the card title on mobile — no label, a touch larger. */
    &:first-child {
      font-size: 1rem;
      padding-bottom: 0.4rem;
    }
    &:first-child::before { display: none; }
  }
`;

const NameCell = styled.div`
  font-weight: 600;
  display: flex;
  align-items: baseline;
  gap: 6px;
  word-break: break-word;
  .star { color: #ca8a04; flex: 0 0 auto; align-self: center; }
`;

const Muted = styled.span`
  color: var(--color-text-muted);
`;

const SummaryCell = styled.div`
  color: var(--color-text);
  font-size: 0.8125rem;
  line-height: 1.45;
  /* No fixed line cap — the full synthesized summary (1-4 sentences) shows, so
     the row's height is driven by its content rather than an arbitrary clamp,
     and tall rows (long names / many topics) fill rather than leaving whitespace. */
`;

const CellTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Tag = styled.span`
  font-size: 0.6875rem;
  padding: 1px 7px;
  border-radius: 99px;
  white-space: nowrap;
  font-weight: 500;
  background: ${props => props.$bg || (props.$accent ? 'rgba(28, 117, 188, 0.1)' : 'rgba(0, 0, 0, 0.05)')};
  color: ${props => props.$fg || (props.$accent ? 'var(--color-accent)' : 'var(--color-text-muted)')};
`;

const TypeText = styled.span`
  overflow-wrap: anywhere;
  .sec { color: var(--color-text-muted); }
`;

// "Joint · N" marker next to the name for multi-stakeholder filings.
const JointPill = styled.span`
  font-size: 0.625rem;
  font-weight: 600;
  white-space: nowrap;
  padding: 1px 6px;
  border-radius: 99px;
  background: rgba(28, 117, 188, 0.1);
  color: var(--color-accent);
  border: 1px solid rgba(28, 117, 188, 0.2);
  flex: 0 0 auto;
  align-self: center;
`;

const GROUP_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'high_level_theme', label: 'Similar topics' },
  { value: 'recommended_action', label: 'Recommended action' },
  { value: 'commenter_type', label: 'Commenter type' },
];

const PAGE_SIZE = 50;

function filtersFromSearchParams(sp) {
  const get = k => sp.get(k);
  const getMulti = k => (get(k) ? get(k).split(',').filter(Boolean) : []);
  return {
    search: get('q') || '',
    clusters: getMulti('clusters'),
    topics: getMulti('topics'),
    recommended_actions: getMulti('actions'),
    commenter_types: getMulti('types'),
    featured_only: get('featured') === '1',
    attachments_only: get('all') !== '1',  // default ON; ?all=1 shows every comment
    cluster_match_mode: get('match') === 'all' ? 'all' : 'any',
    sort: get('sort') || 'cluster_coverage',
    group_by: get('group') || 'none',
  };
}

function filtersToSearchParams(filters) {
  const sp = new URLSearchParams();
  if (filters.search) sp.set('q', filters.search);
  if (filters.clusters.length) sp.set('clusters', filters.clusters.join(','));
  if (filters.topics.length) sp.set('topics', filters.topics.join(','));
  if (filters.recommended_actions.length) sp.set('actions', filters.recommended_actions.join(','));
  if (filters.commenter_types.length) sp.set('types', filters.commenter_types.join(','));
  if (filters.featured_only) sp.set('featured', '1');
  if (!filters.attachments_only) sp.set('all', '1');
  if (filters.cluster_match_mode === 'all') sp.set('match', 'all');
  if (filters.sort !== 'cluster_coverage') sp.set('sort', filters.sort);
  if (filters.group_by && filters.group_by !== 'none') sp.set('group', filters.group_by);
  return sp;
}

// Fixed filter vocabularies (mirror pipeline/classify_submissions.py).
const COMMENTER_CHIPS = [
  'Organized Labor',
  'Employer',
  'Industry Association',
  'Research and academia',
  'Think tank',
  'Advocacy group',
  'Legal practitioner',
  'Individual (visa-holder)',
  'Individual (non-visa-holder)',
  'Other organization',
];
// The 5 substantive recommendation categories (filterable + grouped). The 6th,
// "Other recommendations", is the residual catch-all handled separately below.
const ACTION_CHIPS = [
  'Finalize as written',
  'Finalize with modifications',
  'Explore Experience Benchmarking',
  'Address sector-specific concerns',
  'Withdraw outright',
];
const STATUS_UNSTATED = 'Individual (status unstated)';
const NON_VISA = 'Individual (non-visa-holder)';
const RESIDUAL_ACTION = 'Other recommendations';

// Sentinel value for the "Joint filing" entry in the Commenter type filter. Joint
// status (s.is_joint) is a separate attribute from commenter_type, but we surface
// it as one more option in that same dropdown so it's filterable alongside types.
const JOINT_FILTER_VALUE = '__joint__';
const JOINT_FILTER_LABEL = 'Joint filing';

// status-unstated individuals fold into the non-visa-holder chip for filtering.
const effectiveCommenter = t => (t === STATUS_UNSTATED ? NON_VISA : t);

// Default-order pins (per editor request): these three submissions lead the
// default listing in this exact order, ahead of everything else.
const DEFAULT_PINS = [
  'ETA-2026-0001-1261', // International Brotherhood of Teamsters
  'ETA-2026-0001-1174', // National Foundation for American Policy (NFAP)
  'ETA-2026-0001-1235', // Joint comment of 15 multisector organizations
];
const pinRank = id => {
  const i = DEFAULT_PINS.indexOf(id);
  return i === -1 ? DEFAULT_PINS.length : i;
};

const GROUP_TOOLTIPS = {
  commenter: 'Who submitted the comment — organization type or individual. Status-unstated individuals are counted under "Individual (non-visa-holder)".',
  topic: 'The high-level subject(s) the comment engages with (the 16 topics). Indented entries are subtopics — selecting one filters by that specific subtopic.',
  action: 'What the comment recommends DOL do with the proposed rule (one category per comment, except a sector-specific carve-out can accompany "Withdraw outright" or "Explore Experience Benchmarking").',
};

// Group visible submissions by the selected axis. Returns an array of
// { id, name, items } in display order. For 'high_level_theme', a submission
// appears once under its first (canonical-order) parent theme; submissions
// with no parent themes go to a synthetic "Unsorted" group.
function groupSubmissions(items, groupBy, parentClusters, clustersById) {
  if (groupBy === 'recommended_action') {
    const byAction = new Map();
    for (const s of items) {
      const acts = (s.recommended_actions || []).filter(a => a !== RESIDUAL_ACTION);
      const keys = acts.length ? acts : [RESIDUAL_ACTION];
      for (const k of keys) {
        if (!byAction.has(k)) byAction.set(k, []);
        byAction.get(k).push(s);
      }
    }
    const order = [
      ...ACTION_CHIPS.filter(a => byAction.has(a)),
      ...(byAction.has(RESIDUAL_ACTION) ? [RESIDUAL_ACTION] : []),
    ];
    return order.map(k => ({ id: k, name: k, items: byAction.get(k) }));
  }

  if (groupBy === 'commenter_type') {
    const byType = new Map();
    for (const s of items) {
      const k = effectiveCommenter(s.commenter_type) || 'Unspecified';
      if (!byType.has(k)) byType.set(k, []);
      byType.get(k).push(s);
    }
    const order = [
      ...COMMENTER_CHIPS.filter(t => byType.has(t)),
      ...[...byType.keys()].filter(t => !COMMENTER_CHIPS.includes(t)),
    ];
    return order.map(k => ({ id: k, name: k, items: byType.get(k) }));
  }

  if (groupBy === 'high_level_theme') {
    const parentOrder = (parentClusters || []).map(p => p.id);
    const parentName = Object.fromEntries(
      (parentClusters || []).map(p => [p.id, p.name || p.id])
    );
    const byParent = new Map();
    const unsorted = [];
    for (const s of items) {
      // High-level grouping uses ONLY the independent high-level assignment
      // (s.parent_ids) — we do NOT roll up a comment's low-level themes to
      // their parents. A comment appears under EVERY high-level theme it
      // engages (multi-membership, like the recommended-action grouping), so
      // each theme's group shows its full membership rather than clumping
      // everything into one "primary" theme. Comments the independent pass
      // didn't place fall to "_unsorted".
      const parents = (s.parent_ids || []).filter(pid => parentName[pid] !== undefined);
      if (parents.length === 0) {
        unsorted.push(s);
        continue;
      }
      for (const pid of parents) {
        if (!byParent.has(pid)) byParent.set(pid, []);
        byParent.get(pid).push(s);
      }
    }
    const groups = parentOrder
      .filter(pid => byParent.has(pid))
      .map(pid => ({ id: pid, name: parentName[pid] || pid, items: byParent.get(pid) }));
    if (unsorted.length) {
      groups.push({ id: '_unsorted', name: 'No topic', items: unsorted });
    }
    return groups;
  }

  return [{ id: '_all', name: '', items }];
}

function applyFilters(submissions, filters, searchHits) {
  let result = submissions;

  if (filters.featured_only) {
    result = result.filter(s => s.featured);
  }
  if (filters.attachments_only) {
    // Default view: comments that include an attached document (letters, briefs,
    // studies) — the most substantive submissions. Toggle off to see all.
    result = result.filter(s => s.has_attachment);
  }
  if (filters.commenter_types.length) {
    // "Joint filing" is a pseudo-type (the JOINT_FILTER_VALUE sentinel) layered
    // onto the real commenter types. Selections within this filter are OR'd, so a
    // comment matches if its type is selected OR (joint is selected and it's joint).
    const jointSelected = filters.commenter_types.includes(JOINT_FILTER_VALUE);
    const set = new Set(filters.commenter_types.filter(t => t !== JOINT_FILTER_VALUE));
    result = result.filter(
      s =>
        (s.commenter_type && set.has(effectiveCommenter(s.commenter_type))) ||
        (jointSelected && s.is_joint)
    );
  }
  if (filters.topics.length) {
    const set = new Set(filters.topics);
    result = result.filter(s => (s.parent_ids || []).some(p => set.has(p)));
  }
  if (filters.recommended_actions.length) {
    const set = new Set(filters.recommended_actions);
    result = result.filter(s => (s.recommended_actions || []).some(a => set.has(a)));
  }
  if (filters.clusters.length) {
    if (filters.cluster_match_mode === 'all') {
      result = result.filter(s =>
        filters.clusters.every(cid => s.cluster_ids.includes(cid))
      );
    } else {
      const set = new Set(filters.clusters);
      result = result.filter(s => s.cluster_ids.some(cid => set.has(cid)));
    }
  }
  if (searchHits) {
    result = result.filter(s => searchHits.has(s.doc_id));
  } else {
    // Browse view (no active search): hide comments with no high-level topic
    // assigned — they're low-signal noise in the default listing. They remain
    // fully searchable, so a query (which provides searchHits) still surfaces them.
    result = result.filter(s => (s.parent_ids || []).length > 0);
  }
  return result;
}

function sortSubmissions(items, sort, searchScores) {
  const arr = [...items];
  if (sort === 'cluster_coverage') {
    // Default order (per editor request): three pinned submissions lead in a fixed
    // order (Teamsters → NFAP → 15-org joint comment); then every comment WITH an
    // attachment in reverse order of submission (last filed first); then comments
    // WITHOUT an attachment, also in reverse order of submission. Ties within a
    // posted date break by doc_id descending (later-filed IDs first).
    arr.sort(
      (a, b) =>
        pinRank(a.doc_id) - pinRank(b.doc_id) ||
        (b.has_attachment ? 1 : 0) - (a.has_attachment ? 1 : 0) ||
        (b.posted_date || '').localeCompare(a.posted_date || '') ||
        b.doc_id.localeCompare(a.doc_id)
    );
  } else if (sort === 'num_recommendations') {
    arr.sort(
      (a, b) =>
        (b.featured ? 1 : 0) - (a.featured ? 1 : 0) ||
        (b.num_recommendations || 0) - (a.num_recommendations || 0)
    );
  } else if (sort === 'posted_date_desc') {
    arr.sort((a, b) => (b.posted_date || '').localeCompare(a.posted_date || ''));
  } else if (sort === 'posted_date_asc') {
    arr.sort((a, b) => (a.posted_date || '').localeCompare(b.posted_date || ''));
  } else if (sort === 'word_count_desc') {
    arr.sort((a, b) => (b.word_count || 0) - (a.word_count || 0));
  } else if (sort === 'search_relevance' && searchScores) {
    arr.sort(
      (a, b) =>
        (searchScores.get(b.doc_id) || 0) - (searchScores.get(a.doc_id) || 0)
    );
  } else if (sort === 'name_asc') {
    arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  } else if (sort === 'name_desc') {
    arr.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
  } else if (sort === 'commenter_asc') {
    arr.sort((a, b) =>
      (a.commenter_type || '').localeCompare(b.commenter_type || '') ||
      (a.name || '').localeCompare(b.name || ''));
  } else if (sort === 'commenter_desc') {
    arr.sort((a, b) =>
      (b.commenter_type || '').localeCompare(a.commenter_type || '') ||
      (a.name || '').localeCompare(b.name || ''));
  }
  return arr;
}

// Renders chips that FILL the row's available height, showing only the ones that
// fit COMPLETELY (whole chips) and hiding the rest — no partial chips, no fade.
// The parent .fill is absolutely sized to the row height (desktop CSS); we measure
// each chip and drop any that overflow that height. On mobile the .fill is static
// (full height), so nothing is hidden.
function FillChips({ children }) {
  const ref = useRef(null);
  const moreRef = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const more = moreRef.current;
    if (!el) return undefined;
    const fit = () => {
      const chips = Array.from(el.children).filter(c => c !== more);
      chips.forEach(c => { c.style.display = ''; });
      if (more) more.style.display = 'none';
      const h = el.clientHeight;
      const overflows = c => c.offsetTop + c.offsetHeight > h + 0.5;
      if (!chips.some(overflows)) return;   // everything fits
      // Some chips don't fit: reserve a "+N more" badge and hide chips from the
      // end until both the badge and the remaining chips fit the row height.
      if (!more) return;
      more.style.display = '';
      let hidden = 0;
      for (let i = chips.length - 1; i >= 0; i--) {
        chips[i].style.display = 'none';
        hidden++;
        more.textContent = `+${hidden} more`;
        if (!overflows(more)) break;
      }
    };
    fit();
    let ro;
    let raf = 0;
    if (typeof ResizeObserver !== 'undefined') {
      // Defer to the next frame so our own display mutations inside fit() can't
      // re-enter the observer synchronously ("ResizeObserver loop" warning).
      ro = new ResizeObserver(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(fit);
      });
      ro.observe(el);
    }
    return () => { if (ro) ro.disconnect(); cancelAnimationFrame(raf); };
  }, []);
  return (
    <div className="fill fill-chips" ref={ref}>
      {children}
      <span className="more-chip" ref={moreRef} style={{ display: 'none' }} />
    </div>
  );
}

function SubmissionsDatabase() {
  const { submissions, clustersById, parentClusters, ensureSearchIndex } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => filtersFromSearchParams(searchParams));
  const [openSubmission, setOpenSubmission] = useState(null);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  // Which groups the user has expanded. Grouped views default to all-collapsed
  // (empty set) so every group header is visible at once instead of one open
  // group pushing the rest off-screen.
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());

  // Search state
  const [searchHits, setSearchHits] = useState(null); // Set of doc_ids
  const [searchScores, setSearchScores] = useState(null); // Map<doc_id, score>
  const [searchPending, setSearchPending] = useState(false);

  // Sync filters → URL search params (without remounting)
  useEffect(() => {
    const sp = filtersToSearchParams(filters);
    setSearchParams(sp, { replace: true });
  }, [filters, setSearchParams]);

  // Reset page size when filters change
  useEffect(() => {
    setPageSize(PAGE_SIZE);
  }, [filters]);

  // Start every grouped view collapsed: clear expansions whenever the group-by
  // axis changes so a freshly-selected grouping shows only its headers.
  useEffect(() => {
    setExpandedGroupIds(new Set());
  }, [filters.group_by]);

  // Run search whenever the query changes (debounced via the input itself)
  useEffect(() => {
    let cancelled = false;
    const q = filters.search.trim();
    if (!q) {
      setSearchHits(null);
      setSearchScores(null);
      return undefined;
    }
    setSearchPending(true);
    ensureSearchIndex().then(idx => {
      if (cancelled) return;
      const results = idx.search(q);
      const hits = new Set(results.map(r => r.doc_id));
      const scores = new Map(results.map(r => [r.doc_id, r.score]));
      setSearchHits(hits);
      setSearchScores(scores);
      setSearchPending(false);
    });
    return () => { cancelled = true; };
  }, [filters.search, ensureSearchIndex]);

  // Counts for sidebar (computed from full corpus, not filtered set, so the
  // chip counts always reflect what's available not what's left after filtering)
  // Chip counts from the full corpus so they reflect availability, not the
  // post-filter set.
  const commenterChipCounts = useMemo(() => {
    const c = {};
    for (const s of submissions) {
      const t = effectiveCommenter(s.commenter_type);
      if (t) c[t] = (c[t] || 0) + 1;
    }
    return c;
  }, [submissions]);

  const jointCount = useMemo(
    () => submissions.filter(s => s.is_joint).length,
    [submissions]
  );

  const topicChipCounts = useMemo(() => {
    const c = {};
    for (const s of submissions) {
      for (const p of s.parent_ids || []) c[p] = (c[p] || 0) + 1;
    }
    return c;
  }, [submissions]);

  // Per-subtopic (low-level theme) counts, for the indented entries in the Topic filter.
  const clusterChipCounts = useMemo(() => {
    const c = {};
    for (const s of submissions) {
      for (const cid of s.cluster_ids || []) c[cid] = (c[cid] || 0) + 1;
    }
    return c;
  }, [submissions]);

  const actionChipCounts = useMemo(() => {
    const c = {};
    for (const s of submissions) {
      for (const a of s.recommended_actions || []) c[a] = (c[a] || 0) + 1;
    }
    return c;
  }, [submissions]);

  const hasFeatured = useMemo(
    () => submissions.some(s => s.featured),
    [submissions]
  );


  // Filter and sort
  const filteredSortedSubmissions = useMemo(() => {
    const filtered = applyFilters(submissions, filters, searchHits);
    const sortMode =
      filters.search && searchScores ? 'search_relevance' : filters.sort;
    return sortSubmissions(filtered, sortMode, searchScores);
  }, [submissions, filters, searchHits, searchScores]);

  const paginatedSlice = filteredSortedSubmissions.slice(0, pageSize);

  const removeFilterValue = (key, value) => {
    setFilters(f => ({
      ...f,
      [key]: f[key].filter(v => v !== value),
    }));
  };

  const toggleMulti = (key, value) => {
    setFilters(f => {
      const cur = new Set(f[key]);
      if (cur.has(value)) cur.delete(value); else cur.add(value);
      return { ...f, [key]: Array.from(cur) };
    });
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      clusters: [],
      topics: [],
      recommended_actions: [],
      commenter_types: [],
      featured_only: false,
      attachments_only: true,
      cluster_match_mode: 'any',
      sort: 'cluster_coverage',
      group_by: 'none',
    });
  };

  const parentNameById = useMemo(
    () => Object.fromEntries((parentClusters || []).map(p => [p.id, p.name || p.id])),
    [parentClusters]
  );

  // Topic filter options: each of the 16 topics, with its subtopics (if any)
  // listed immediately beneath it as indented, separately-selectable entries.
  // Topic values toggle filters.topics; subtopic values toggle filters.clusters.
  const topicFilterOptions = useMemo(() => {
    const opts = [];
    for (const p of parentClusters || []) {
      opts.push({ value: p.id, label: p.name, count: topicChipCounts[p.id] || 0, tooltip: p.description });
      for (const cid of p.children || []) {
        opts.push({
          value: cid,
          label: clustersById[cid]?.name || cid,
          count: clusterChipCounts[cid] || 0,
          tooltip: clustersById[cid]?.description,
          indent: true,
        });
      }
    }
    return opts;
  }, [parentClusters, clustersById, topicChipCounts, clusterChipCounts]);

  // A single value space for the Topic dropdown spanning topics + subtopics.
  const topicSelectedValues = useMemo(
    () => [...filters.topics, ...filters.clusters],
    [filters.topics, filters.clusters]
  );
  const clusterIdSet = useMemo(() => new Set((parentClusters || []).flatMap(p => p.children || [])), [parentClusters]);
  // topic id -> its subtopic (child) ids, for nesting in the grouped view.
  const childrenByParent = useMemo(
    () => Object.fromEntries((parentClusters || []).map(p => [p.id, p.children || []])),
    [parentClusters]
  );
  const onToggleTopicOrSubtopic = v =>
    clusterIdSet.has(v) ? toggleMulti('clusters', v) : toggleMulti('topics', v);
  const clearTopicsAndSubtopics = () =>
    setFilters(f => ({ ...f, topics: [], clusters: [] }));

  // Each pill carries the same color as its corresponding chip in the table,
  // for visual continuity between the filter and the results.
  const NEUTRAL_PILL = { bg: '#6b7280', fg: '#ffffff' };
  const activePills = [];
  // Topics and subtopics are grouped into ONE chip per parent topic: the parent
  // segment (removable only if the topic itself is selected — otherwise it's just
  // a context label for the subtopic) followed by each selected subtopic. So a
  // topic and its subtopic never show two separate "Sector-specific impacts" chips.
  const topicGroups = new Map();  // pid -> { topicSelected, subs: [{cid,label}] }
  const ensureGroup = pid => {
    if (!topicGroups.has(pid)) topicGroups.set(pid, { topicSelected: false, subs: [] });
    return topicGroups.get(pid);
  };
  filters.topics.forEach(pid => { ensureGroup(pid).topicSelected = true; });
  filters.clusters.forEach(cid => {
    const sc = clustersById[cid];
    const pid = (sc?.parent_ids || [])[0] || '_none';
    ensureGroup(pid).subs.push({ cid, label: sc?.name || cid });
  });
  topicGroups.forEach((g, pid) => {
    activePills.push({
      kind: 'topicgroup',
      pid: pid !== '_none' ? pid : null,
      parentLabel: pid !== '_none' ? (parentNameById[pid] || pid) : null,
      parentColor: pid !== '_none' ? themeColor(pid) : NEUTRAL_PILL,
      topicSelected: g.topicSelected,
      subs: g.subs,
    });
  });
  filters.recommended_actions.forEach(a => {
    activePills.push({ label: ACTION_SHORTHAND[a] || a, color: actionColor(a), remove: () => removeFilterValue('recommended_actions', a) });
  });
  filters.commenter_types.forEach(t => {
    activePills.push({
      label: t === JOINT_FILTER_VALUE ? JOINT_FILTER_LABEL : t,
      color: COMMENTER_COLOR,
      remove: () => removeFilterValue('commenter_types', t),
    });
  });

  const toggleGroup = (id) => {
    setExpandedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const groupBy = filters.group_by || 'none';
  const isGrouped = groupBy !== 'none';

  // Column sorting (Name / Commenter type). filters.sort holds e.g. "name_asc".
  const sortMatch = (filters.sort || '').match(/^(name|commenter)_(asc|desc)$/);
  const sortKey = sortMatch ? sortMatch[1] : null;
  const sortDir = sortMatch ? sortMatch[2] : null;
  const onHeaderSort = (key) => setFilters(f => ({
    ...f,
    sort: f.sort === `${key}_asc` ? `${key}_desc` : `${key}_asc`,
  }));

  // Low-level subtopics are filtered via the indented entries under the Topic
  // dropdown (onToggleTopicOrSubtopic). Multiple selections match ANY (union).
  // Topics are also selectable straight from the detail drawer.
  const onSelectTopic = (pid) => toggleMulti('topics', pid);
  const groups = useMemo(
    () => groupSubmissions(filteredSortedSubmissions, groupBy, parentClusters, clustersById),
    [filteredSortedSubmissions, groupBy, parentClusters, clustersById]
  );

  // In grouped mode we render every group fully (no per-group pagination).
  // In flat mode we keep the "Load more" pagination.
  const renderSlice = isGrouped ? filteredSortedSubmissions : paginatedSlice;

  // Export the currently-filtered (and sorted) comments as CSV. Columns mirror
  // the visible table plus the docket Document ID for traceability.
  const downloadCsv = () => {
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['Document ID', 'Name', 'Recommended actions', 'Summary', 'Topics', 'Commenter type'];
    const lines = [headers.join(',')];
    for (const s of filteredSortedSubmissions) {
      const actions = (s.recommended_actions || []).filter(a => a !== RESIDUAL_ACTION).join('; ');
      const topics = (s.parent_ids || []).map(pid => parentNameById[pid] || pid).join('; ');
      const ctype = `${s.commenter_type || ''}${s.employer_subtype ? ` (${s.employer_subtype})` : ''}`;
      lines.push([s.doc_id, s.name, actions, s.main_recommendation || '', topics, ctype].map(esc).join(','));
    }
    // Prepend a UTF-8 BOM so Excel reads accented names/quotes correctly.
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pwd-nprm-comments-${filteredSortedSubmissions.length}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const renderTable = (items) => (
    <TableScroll>
      <ResultsTable>
        <thead>
          <tr>
            <SortableTh $active={sortKey === 'name'} $dir={sortDir} onClick={() => onHeaderSort('name')}>
              Name {sortKey === 'name' && <SortArrow>{sortDir === 'asc' ? '↑' : '↓'}</SortArrow>}
            </SortableTh>
            <Th className="col-action">Recommended action</Th>
            <Th>Summary</Th>
            <Th className="col-topic">Topics</Th>
            <SortableTh $active={sortKey === 'commenter'} $dir={sortDir} onClick={() => onHeaderSort('commenter')} className="col-type">
              Commenter type {sortKey === 'commenter' && <SortArrow>{sortDir === 'asc' ? '↑' : '↓'}</SortArrow>}
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {items.map(s => {
            const topics = (s.parent_ids || []).map(pid => ({ id: pid, name: parentNameById[pid] || pid }));
            const actions = (s.recommended_actions || []).filter(a => a !== RESIDUAL_ACTION);
            return (
              <Tr key={s.doc_id} onClick={() => setOpenSubmission(s)}>
                <Td data-label="Name">
                  <NameCell>
                    {s.featured && <Star className="star" size={13} fill="#ca8a04" />}
                    <span>{s.name}</span>
                    {s.is_joint && (
                      <JointPill title={`Joint filing by ${s.signatory_count} organizations`}>
                        Joint · {s.signatory_count}
                      </JointPill>
                    )}
                  </NameCell>
                </Td>
                <Td data-label="Recommended action" className="col-action">
                  {actions.length
                    ? <CellTags>{actions.map(a => {
                        const c = actionColor(a);
                        return <Tag $bg={c.bg} $fg={c.fg} key={a}>{ACTION_SHORTHAND[a] || a}</Tag>;
                      })}</CellTags>
                    : <Muted>—</Muted>}
                </Td>
                <Td data-label="Summary" className="col-summary">
                  <div className="fill">
                    {s.main_recommendation
                      ? <SummaryCell>{s.main_recommendation}</SummaryCell>
                      : <Muted>—</Muted>}
                  </div>
                </Td>
                <Td data-label="Topic" className="col-topic">
                  {topics.length
                    ? <FillChips>{topics.map(t => {
                        const c = themeColor(t.id);
                        return (
                          <Tag key={t.id} $bg={c.bg} $fg={c.fg}>
                            {THEME_SHORTHAND[t.id] || t.name}
                          </Tag>
                        );
                      })}</FillChips>
                    : <div className="fill"><Muted>—</Muted></div>}
                </Td>
                <Td data-label="Commenter type" className="col-type">
                  <TypeText>
                    {s.commenter_type || '—'}
                    {s.employer_subtype ? ` (${s.employer_subtype})` : ''}
                    {(s.commenter_type_secondary || []).length > 0 && (
                      <span className="sec">{s.commenter_type_secondary.map(t => ` · ${t}`).join('')}</span>
                    )}
                  </TypeText>
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </ResultsTable>
    </TableScroll>
  );

  // Body of one group in the grouped view. For the "Similar topics" grouping,
  // a topic that has subtopics renders its submissions split into subtopic
  // sub-sections (plus a "no subtopic" bucket); everything else renders flat.
  const renderGroupBody = (g) => {
    const children = groupBy === 'high_level_theme' ? (childrenByParent[g.id] || []) : [];
    if (children.length === 0) return renderTable(g.items);

    const subSections = children
      .map(cid => ({
        cid,
        name: clustersById[cid]?.name || cid,
        items: g.items.filter(s => (s.cluster_ids || []).includes(cid)),
      }))
      .filter(sub => sub.items.length > 0);
    const rest = g.items.filter(s => !children.some(cid => (s.cluster_ids || []).includes(cid)));

    return (
      <>
        {subSections.map(sub => (
          <SubGroupWrap key={sub.cid}>
            <SubGroupHeader>
              {sub.name} <span className="sub-count">· {sub.items.length.toLocaleString()}</span>
            </SubGroupHeader>
            {renderTable(sub.items)}
          </SubGroupWrap>
        ))}
        {rest.length > 0 && (
          <SubGroupWrap>
            <SubGroupHeader>
              {g.name} — no subtopic <span className="sub-count">· {rest.length.toLocaleString()}</span>
            </SubGroupHeader>
            {renderTable(rest)}
          </SubGroupWrap>
        )}
      </>
    );
  };

  return (
    <>
      <TopBar>
        <TopBarInner>
          <TopSearchWrap>
            <TopSearchIcon><Search size={16} /></TopSearchIcon>
            <TopSearchInput
              type="search"
              placeholder="Search comment text…"
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            />
            {filters.search && (
              <TopSearchClear
                type="button"
                onClick={() => setFilters(f => ({ ...f, search: '' }))}
                aria-label="Clear search"
              >
                <X size={14} />
              </TopSearchClear>
            )}
          </TopSearchWrap>

          <TopMeta>
            <strong>{filteredSortedSubmissions.length.toLocaleString()}</strong> of{' '}
            {submissions.length.toLocaleString()} public submissions
          </TopMeta>

          <DownloadButton
            onClick={downloadCsv}
            disabled={filteredSortedSubmissions.length === 0}
            title="Download the current (filtered) comments as a CSV"
          >
            <Download size={14} /> CSV
          </DownloadButton>

          <MobileFilterButton onClick={() => setIsMobileFiltersOpen(o => !o)}>
            {isMobileFiltersOpen ? <X size={14} /> : <FilterIcon size={14} />}
            {isMobileFiltersOpen ? 'Hide' : 'Filters'}
          </MobileFilterButton>
        </TopBarInner>
      </TopBar>

      <FilterBar $mobileOpen={isMobileFiltersOpen}>
        <FilterRow>
          <FilterDropdown
            label="Commenter type"
            tooltip={GROUP_TOOLTIPS.commenter}
            options={[
              ...COMMENTER_CHIPS.map(t => ({ value: t, label: t, count: commenterChipCounts[t] || 0 })),
              { value: JOINT_FILTER_VALUE, label: JOINT_FILTER_LABEL, count: jointCount },
            ]}
            selectedValues={filters.commenter_types}
            onToggle={v => toggleMulti('commenter_types', v)}
            onClear={() => setFilters(f => ({ ...f, commenter_types: [] }))}
          />
          <FilterDropdown
            label="Topic"
            tooltip={GROUP_TOOLTIPS.topic}
            options={topicFilterOptions}
            selectedValues={topicSelectedValues}
            onToggle={onToggleTopicOrSubtopic}
            onClear={clearTopicsAndSubtopics}
          />
          <FilterDropdown
            label="Recommended action"
            tooltip={GROUP_TOOLTIPS.action}
            options={ACTION_CHIPS.map(a => ({ value: a, label: a, count: actionChipCounts[a] || 0 }))}
            selectedValues={filters.recommended_actions}
            onToggle={v => toggleMulti('recommended_actions', v)}
            onClear={() => setFilters(f => ({ ...f, recommended_actions: [] }))}
          />
          <FilterDropdown
            label="Group by"
            multi={false}
            tooltip="Group the results table by a shared attribute."
            options={GROUP_OPTIONS}
            selectedValues={groupBy === 'none' ? [] : [groupBy]}
            onToggle={v => setFilters(f => ({ ...f, group_by: v }))}
          />
          <InlineToggle>
            <input
              type="checkbox"
              checked={filters.attachments_only}
              onChange={e => setFilters(f => ({ ...f, attachments_only: e.target.checked }))}
            />
            Comments with attachments only
          </InlineToggle>
          {hasFeatured && (
            <InlineToggle>
              <input
                type="checkbox"
                checked={filters.featured_only}
                onChange={e => setFilters(f => ({ ...f, featured_only: e.target.checked }))}
              />
              Featured only
            </InlineToggle>
          )}
          <ResetLink onClick={resetFilters}>Reset</ResetLink>
        </FilterRow>
      </FilterBar>

      <Layout>
        <Main>
          {filters.search && (
            <SearchStatus>
              {searchPending
                ? `Searching for "${filters.search}"…`
                : `Showing best matches for "${filters.search}"`}
            </SearchStatus>
          )}

          {activePills.length > 0 && (
            <ActiveFilterPills>
              {activePills.map((p, i) => (
                p.kind === 'topicgroup' ? (
                  <CompoundPill key={i}>
                    {p.parentLabel && (
                      p.topicSelected ? (
                        // Topic itself is filtered → removable, full-color segment.
                        <button
                          type="button"
                          className="seg parent"
                          style={{ background: p.parentColor.bg, color: p.parentColor.fg }}
                          title={`Remove “${p.parentLabel}” topic filter`}
                          onClick={() => removeFilterValue('topics', p.pid)}
                        >
                          {p.parentLabel} <X size={10} />
                        </button>
                      ) : (
                        // Only a subtopic is filtered → parent is just a context
                        // label (dimmed, no ✕): the topic as a whole is NOT filtered.
                        <span
                          className="seg parent label-only"
                          style={{ background: p.parentColor.bg, color: p.parentColor.fg }}
                          title={`${p.parentLabel} (category — not filtered; only the subtopic below is)`}
                        >
                          {p.parentLabel}
                        </span>
                      )
                    )}
                    {p.subs.map(s => (
                      <button
                        key={s.cid}
                        type="button"
                        className="seg child"
                        title={`Remove “${s.label}” filter`}
                        onClick={() => removeFilterValue('clusters', s.cid)}
                      >
                        {s.label} <X size={10} />
                      </button>
                    ))}
                  </CompoundPill>
                ) : (
                  <ActivePill key={i} $bg={p.color?.bg} $fg={p.color?.fg} onClick={p.remove}>
                    {p.label} <X size={10} />
                  </ActivePill>
                )
              ))}
            </ActiveFilterPills>
          )}

          {filteredSortedSubmissions.length === 0 ? (
            <Empty>
              No submissions match the current filters.
            </Empty>
          ) : isGrouped ? (
            <>
              {groups.map(g => {
                const collapsed = !expandedGroupIds.has(g.id);
                return (
                  <GroupSection key={g.id}>
                    <GroupHeader onClick={() => toggleGroup(g.id)}>
                      {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                      <GroupName>{g.name}</GroupName>
                      <GroupCount>{g.items.length.toLocaleString()}</GroupCount>
                    </GroupHeader>
                    {!collapsed && renderGroupBody(g)}
                  </GroupSection>
                );
              })}
            </>
          ) : (
            renderTable(renderSlice)
          )}

          {!isGrouped && pageSize < filteredSortedSubmissions.length && (
            <LoadMore onClick={() => setPageSize(p => p + PAGE_SIZE)}>
              Load {Math.min(PAGE_SIZE, filteredSortedSubmissions.length - pageSize)} more
            </LoadMore>
          )}
        </Main>

        <DetailDrawer
          submission={openSubmission}
          parentNameById={parentNameById}
          onClose={() => setOpenSubmission(null)}
          onSelectTopic={onSelectTopic}
          activeTopicIds={filters.topics}
        />
      </Layout>
    </>
  );
}

export default SubmissionsDatabase;
